/**
 * PaneShell — the right-side sidebar mirror.
 *
 * Layout states (mirrors the left sidebar):
 *   • open       — 520px (default desktop)
 *   • collapsed  — 56px rail with vertical "Omni Pane" label only
 *   • fullscreen — covers the entire viewport above the chat
 *   • hidden     — disabled via tweak / off entirely
 *
 * Keyboard shortcut: Cmd+. / Ctrl+. toggles open/collapsed.
 *                    Cmd+Shift+. / Ctrl+Shift+. toggles fullscreen.
 *
 * State machine — `open`, `collapsed`, `fullscreen` are persisted
 * to localStorage so the layout survives reload.
 *
 * Spec: Docs/architecture/24-omni-pane.md §5 ("Shell + layout").
 */
import * as React from "react";
import {
  Maximize2,
  Minimize2,
  PanelRight,
  PanelRightClose,
  Plus,
  Trash2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/cn";
import { HOME_PATH } from "@/lib/routes";
import { composeInChat, refreshRequest } from "./askInChat";
import { PaneGrid, compactUp, type TilePatch } from "./PaneGrid";
import { listTileKinds, resolveTile } from "./registry";
import { usePaneTiles } from "./usePaneTiles";
import type { PaneTile, TileKind } from "./types";

const STATE_KEY = "omni:pane-state";

// Below this width the pane behaves as an off-canvas full-screen
// drawer (matching how the left Sidebar goes off-canvas on mobile)
// instead of an inline column. lg breakpoint − 1px.
const MOBILE_QUERY = "(max-width: 1023px)";

/**
 * Single source of truth for the pane's visual state. Modelled as one
 * enum (not three independent booleans) so it is impossible to land in
 * the old "open=false + collapsed=false" trap where the pane rendered
 * as a 0px-wide invisible box with no on-screen way to reopen it.
 *
 *   • open       — 520px full canvas
 *   • collapsed  — 56px rail (always shows an expand button — the pane
 *                  can NEVER fully disappear, so it's always recoverable)
 *   • fullscreen — covers the viewport
 *
 * Feature-flagging the pane off entirely is the `enabled` prop's job.
 */
export type PaneVisualState = "collapsed" | "open" | "fullscreen";

interface PaneShellProps {
  /**
   * When `false`, the pane renders nothing — used to feature-flag
   * the pane off entirely. Default true.
   */
  enabled?: boolean;
}

export function PaneShell({ enabled = true }: PaneShellProps) {
  const isMobile = useIsMobile();

  // ── persisted visual state (single enum) ───────────────────────────
  // Never auto-open on mobile: a 520px / full-screen panel popping over
  // a phone on every page load is hostile. On mobile we always boot to
  // the collapsed launcher regardless of the persisted desktop
  // preference; the user opens it explicitly via the edge launcher.
  // Desktop honors the stored state.
  const [state, setState] = React.useState<PaneVisualState>(() => {
    if (
      typeof window !== "undefined" &&
      window.matchMedia(MOBILE_QUERY).matches
    ) {
      return "collapsed";
    }
    return readState(STATE_KEY, "open");
  });
  React.useEffect(() => writeState(STATE_KEY, state), [state]);

  const open = state === "open";
  const collapsed = state === "collapsed";
  const fullscreen = state === "fullscreen";

  // On mobile an "open" pane is a full-screen drawer overlay (not a
  // 520px inline column that would crush the page). Desktop only goes
  // overlay for explicit fullscreen. Either way the overlay always
  // renders its header + content, so it can never be a blank trap.
  const asOverlay = fullscreen || (isMobile && open);

  // ── keyboard shortcuts ─────────────────────────────────────────────
  // Cmd/Ctrl + .             → toggle open ↔ collapsed (rail)
  // Cmd/Ctrl + Shift + .     → toggle fullscreen ↔ open
  React.useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key !== "." && e.key !== ">") return;
      e.preventDefault();
      if (e.shiftKey) {
        setState((s) => (s === "fullscreen" ? "open" : "fullscreen"));
      } else {
        setState((s) => (s === "collapsed" ? "open" : "collapsed"));
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [enabled]);

  // ── data ───────────────────────────────────────────────────────────
  const {
    tiles,
    loading,
    error,
    create,
    update,
    remove,
    clear,
  } = usePaneTiles();

  // Track tiles that were just spawned so the grid can animate them
  // in. Drops the id after 700ms (matches the CSS animation).
  const [newIds, setNewIds] = React.useState<Set<string>>(() => new Set());
  const markNew = React.useCallback((id: string) => {
    setNewIds((prev) => new Set(prev).add(id));
    window.setTimeout(() => {
      setNewIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 700);
  }, []);

  const [addOpen, setAddOpen] = React.useState(false);
  const navigate = useNavigate();

  // The server places and sizes a new tile (first free slot, sized to the
  // kind); a second size table here would drift from it.
  const handleAdd = async (kind: TileKind) => {
    const tile = await create({ kind, title: resolveTile(kind).title, source: "you · added" });
    if (tile) markNew(tile.id);
    setAddOpen(false);
  };

  const handleRefresh = (tile: PaneTile) => {
    const refresh = tile.data.refresh;
    if (typeof refresh !== "string") return;
    const text = refreshRequest(tile, refresh);
    if (!composeInChat(text)) navigate(HOME_PATH, { state: { compose: text } });
    if (isMobile) setState("collapsed"); // the drawer covers the composer
  };

  const handleMove = (id: string, geom: { x: number; y: number }) => {
    void update(id, geom);
  };
  const handleResize = (id: string, geom: { w: number; h: number }) => {
    void update(id, geom);
  };
  const handleExpand = (id: string) => {
    void update(id, { x: 0, w: 12, h: Math.max(8, tiles.find((t) => t.id === id)?.h ?? 4) });
  };
  const handleUpdate = (id: string, patch: TilePatch) => {
    void update(id, patch);
  };

  // Compact-up before rendering so gaps from deletes / resizes fill
  // immediately, even before the server round-trip lands. Called
  // BEFORE the `!enabled` early-return so the hook order stays
  // stable across the on/off toggle.
  const compacted = React.useMemo(() => compactUp(tiles), [tiles]);
  const unpinned = compacted.filter((t) => !t.pinned).length;

  if (!enabled) return null;

  return (
    <>
      <aside
        data-pane-state={state}
        data-pane-overlay={asOverlay ? "true" : "false"}
        className={cn(
          "relative flex h-full flex-col border-l border-border bg-bg transition-[width] duration-200",
          asOverlay
            ? // Full-screen drawer (mobile-open or desktop-fullscreen).
              "fixed inset-0 z-40 w-full"
            : collapsed
              ? // On mobile a collapsed pane takes NO inline space — the
                // edge launcher (below) is the only affordance. On
                // desktop it's the 56px rail.
                isMobile
                ? "w-0 overflow-hidden border-l-0"
                : "w-[56px]"
              : "w-[520px]",
        )}
        aria-label="Omni Pane"
      >
        {/* Collapsed-rail content (desktop only) — always shows an
            expand button, so the pane can never become unrecoverable.
            On mobile the rail is replaced by the edge launcher. */}
        {collapsed && !isMobile && (
          <CollapsedRail
            tileCount={compacted.length}
            onExpand={() => setState("open")}
          />
        )}

      {/* Full pane content (open or fullscreen) */}
      {(open || fullscreen) && (
        <>
          <header className="flex h-[44px] flex-shrink-0 items-center gap-2 border-b border-border bg-bg-sunken px-3">
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-fg">
              <PanelRight size={14} />
              <span>Omni Pane</span>
            </div>
            <span className="ml-2 font-mono text-[10px] tracking-[0.1em] text-fg-faint">
              {compacted.length} tile{compacted.length === 1 ? "" : "s"} · 12-col
            </span>
            <div className="ml-auto flex items-center gap-1">
              {/* Fullscreen toggle is desktop-only — on mobile the pane
                  is already a full-screen drawer. */}
              {!isMobile && (
                <RailBtn
                  label={fullscreen ? "Exit fullscreen" : "Fullscreen"}
                  onClick={() =>
                    setState((s) => (s === "fullscreen" ? "open" : "fullscreen"))
                  }
                >
                  {fullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
                </RailBtn>
              )}
              {/* Pinned is the person's "keep this": Clear takes the rest. */}
              {unpinned > 0 && (
                <RailBtn
                  label={unpinned === compacted.length ? "Clear all" : "Clear unpinned"}
                  onClick={() => void clear(true)}
                  danger
                >
                  <Trash2 size={13} />
                </RailBtn>
              )}
              <RailBtn
                label={isMobile ? "Close pane" : "Collapse pane"}
                onClick={() => setState("collapsed")}
              >
                <PanelRightClose size={13} />
              </RailBtn>
            </div>
          </header>

          {error && (
            <div className="flex flex-shrink-0 items-center gap-2 border-b border-border bg-danger/10 px-3 py-1.5 font-mono text-[11px] text-danger">
              <span>·</span> {error}
            </div>
          )}

          <div className="relative min-h-0 flex-1">
            {loading && compacted.length === 0 ? (
              <div className="flex h-full items-center justify-center font-mono text-[11px] uppercase tracking-[0.2em] text-fg-faint">
                loading…
              </div>
            ) : compacted.length === 0 ? (
              <EmptyState onAdd={() => setAddOpen(true)} />
            ) : (
              <PaneGrid
                tiles={compacted}
                onMove={handleMove}
                onResize={handleResize}
                onRemove={(id) => void remove(id)}
                onExpand={handleExpand}
                onUpdate={handleUpdate}
                onRefresh={handleRefresh}
                newTileIds={newIds}
              />
            )}

            {/* Add FAB */}
            {compacted.length > 0 && (
              <button
                type="button"
                onClick={() => setAddOpen((v) => !v)}
                aria-label="Add tile"
                className="absolute bottom-4 right-4 z-30 flex h-11 w-11 items-center justify-center bg-accent text-accent-fg shadow-lg shadow-accent/30 transition hover:-translate-y-0.5 hover:shadow-xl"
              >
                <Plus size={18} />
              </button>
            )}

            {addOpen && (
              <AddMenu
                onPick={(k) => void handleAdd(k)}
                onClose={() => setAddOpen(false)}
              />
            )}
          </div>
        </>
      )}
      </aside>

      {/* Mobile edge launcher — the only way to open the pane on a
          phone, since there's no keyboard shortcut and the collapsed
          rail is suppressed on mobile. Vertically centred on the right
          edge; sits below the command palette / dialogs (z-30). */}
      {isMobile && collapsed && (
        <button
          type="button"
          onClick={() => setState("open")}
          aria-label="Open Omni Pane"
          className="fixed right-0 top-1/2 z-30 flex -translate-y-1/2 items-center gap-1 border border-r-0 border-border bg-bg-elevated py-3 pl-1.5 pr-1 text-fg-muted shadow-lg transition hover:text-accent"
        >
          <PanelRight size={16} />
        </button>
      )}
    </>
  );
}

// ─── persisted state helpers ────────────────────────────────────────────

const VALID_STATES: ReadonlySet<string> = new Set([
  "open",
  "collapsed",
  "fullscreen",
]);

function readState(key: string, fallback: PaneVisualState): PaneVisualState {
  try {
    const v = localStorage.getItem(key);
    if (v && VALID_STATES.has(v)) return v as PaneVisualState;
    return fallback;
  } catch {
    return fallback;
  }
}
function writeState(key: string, value: PaneVisualState) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode etc — silently skip */
  }
}

/**
 * Reactive viewport-width media query. Re-renders the pane when the
 * viewport crosses the mobile breakpoint (rotation, window resize,
 * desktop responsive-mode toggling) so the inline-vs-overlay decision
 * always matches the current width.
 */
function useIsMobile(query: string = MOBILE_QUERY): boolean {
  const get = () =>
    typeof window !== "undefined" && window.matchMedia(query).matches;
  const [matches, setMatches] = React.useState<boolean>(get);
  React.useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

// ─── subviews ───────────────────────────────────────────────────────────

function CollapsedRail({
  tileCount,
  onExpand,
}: {
  tileCount: number;
  onExpand: () => void;
}) {
  return (
    <div className="flex h-full w-full flex-col items-center gap-3 py-3">
      <button
        type="button"
        onClick={onExpand}
        aria-label="Expand Omni Pane"
        className="flex h-9 w-9 items-center justify-center text-fg-muted transition hover:bg-bg-elevated hover:text-fg"
      >
        <PanelRight size={14} />
      </button>
      <span
        className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-faint"
        style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
      >
        Omni Pane · {tileCount} tile{tileCount === 1 ? "" : "s"}
      </span>
    </div>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
      <div className="text-[14px] font-medium text-fg">
        Nothing on your canvas yet
      </div>
      <p className="max-w-[280px] text-[12.5px] leading-relaxed text-fg-muted">
        Ask in chat to <em>"show the trains as a table"</em> or <em>"make me a
        packing checklist"</em> — tables, checklists, charts, links and cards
        land here. Or add a note yourself.
      </p>
      <button
        type="button"
        onClick={onAdd}
        className="mt-1 flex h-8 items-center gap-2 bg-fg px-3 text-[12.5px] font-medium text-bg transition hover:bg-accent hover:text-accent-fg"
      >
        <Plus size={14} /> Add a tile
      </button>
    </div>
  );
}

function AddMenu({
  onPick,
  onClose,
}: {
  onPick: (kind: TileKind) => void;
  onClose: () => void;
}) {
  const kinds = listTileKinds();
  return (
    <>
      <div className="fixed inset-0 z-20" onClick={onClose} />
      <div className="absolute bottom-20 right-4 z-30 w-[280px] border border-border bg-bg-elevated p-2 shadow-2xl">
        <div className="px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint">
          Add tile · {kinds.length} {kinds.length === 1 ? "kind" : "kinds"}
        </div>
        <div className="grid grid-cols-2 gap-1">
          {kinds.map(({ kind, def }) => {
            const Icon = def.Icon;
            return (
              <button
                key={kind}
                type="button"
                onClick={() => onPick(kind)}
                className="group flex items-center gap-2 px-2.5 py-2 text-left text-[12.5px] text-fg transition hover:bg-accent/10 hover:text-accent"
              >
                <span className="flex h-6 w-6 items-center justify-center bg-bg-sunken text-fg-muted transition group-hover:bg-accent group-hover:text-accent-fg">
                  <Icon size={13} />
                </span>
                {def.title}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

function RailBtn({
  children,
  label,
  onClick,
  danger,
}: {
  children: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        "flex h-7 w-7 items-center justify-center text-fg-muted transition",
        danger ? "hover:bg-danger/15 hover:text-danger" : "hover:bg-bg-elevated hover:text-fg",
      )}
    >
      {children}
    </button>
  );
}
